import { useTranslation } from 'react-i18next';
import { Database, Eye, MessageSquare } from 'lucide-react';
import {
  eventLabel,
  formatAuditDateTime,
  isDbChange,
  loginFailureReason,
  sessionsTerminatedCause,
  shortId,
  type AuditLogEvent,
} from '@entities/audit-log';
import {
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  CopyFeedbackIcon,
} from '@shared/ui';
import { useCopyToClipboard } from '@shared/lib/use-copy-to-clipboard';
import { AuditActorCell, AuditOutcomeBadge } from '@entities/audit-log';

interface AuditLogTableProps {
  items: AuditLogEvent[];
  isLoading: boolean;
  isEmpty: boolean;
  hasFilters: boolean;
  hasMore: boolean;
  isValidating: boolean;
  projectName: (projectId: string) => string;
  onOpen: (event: AuditLogEvent) => void;
  onActor: (userId: number) => void;
  onLoadMore: () => void;
}

export function AuditLogTable({
  items,
  isLoading,
  isEmpty,
  hasFilters,
  hasMore,
  isValidating,
  projectName,
  onOpen,
  onActor,
  onLoadMore,
}: AuditLogTableProps) {
  const { t } = useTranslation('cabinet');
  const { copy, isCopied } = useCopyToClipboard();
  const copiedMessage = t('auditLog.copied');

  if (isLoading) {
    return (
      <div className="surface-card space-y-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} className="h-12 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="surface-card py-12 text-center text-sm text-muted-foreground">
        {hasFilters ? t('auditLog.emptyFiltered') : t('auditLog.emptyPeriod')}
      </div>
    );
  }

  return (
    <div className="surface-card min-w-0 space-y-3">
      <div className="space-y-2 lg:hidden">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            className="w-full rounded-xl border border-border p-3 text-left transition-colors duration-fast ease-standard hover:bg-accent"
            onClick={() => onOpen(item)}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{formatAuditDateTime(item.occurredAt)}</p>
                <p className="truncate text-sm font-medium">{eventLabel(item.eventType, item.description, t)}</p>
              </div>
              <AuditOutcomeBadge outcome={item.outcome} />
            </div>
            <div className="mt-2">
              <AuditActorCell actor={item.actor} />
            </div>
          </button>
        ))}
      </div>

      <div className="hidden lg:block">
        <TooltipProvider delayDuration={200}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('auditLog.columns.time')}</TableHead>
                <TableHead>{t('auditLog.columns.event')}</TableHead>
                <TableHead>{t('auditLog.columns.category')}</TableHead>
                <TableHead>{t('auditLog.columns.outcome')}</TableHead>
                <TableHead>{t('auditLog.columns.actor')}</TableHead>
                <TableHead>{t('auditLog.columns.target')}</TableHead>
                <TableHead>{t('auditLog.columns.project')}</TableHead>
                <TableHead>{t('auditLog.columns.ip')}</TableHead>
                <TableHead>{t('auditLog.columns.reason')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => {
                const failure = loginFailureReason(item, t);
                const terminationCause = sessionsTerminatedCause(item, t);
                return (
                  <TableRow
                    key={item.id}
                    className="cursor-pointer hover:bg-accent/60"
                    onClick={() => onOpen(item)}
                  >
                    <TableCell className="whitespace-nowrap">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>{formatAuditDateTime(item.occurredAt)}</span>
                        </TooltipTrigger>
                        <TooltipContent>{item.occurredAt}</TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs">
                        <p className="truncate font-medium">
                          {eventLabel(item.eventType, item.description, t)}
                        </p>
                        <p className="truncate font-mono text-xs text-muted-foreground">{item.eventType}</p>
                        {failure ? <p className="text-xs text-muted-foreground">{failure}</p> : null}
                        {isDbChange(item) ? (
                          <span className="mt-1 inline-flex items-center gap-1 text-xs text-pending-foreground">
                            <Database className="h-3 w-3" />
                            {t('auditLog.dbChange')}
                          </span>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">
                        {t(`auditLog.categories.${item.category}`, { defaultValue: item.category })}
                      </span>
                    </TableCell>
                    <TableCell>
                      <AuditOutcomeBadge outcome={item.outcome} />
                    </TableCell>
                    <TableCell>
                      <AuditActorCell actor={item.actor} onUserClick={onActor} />
                    </TableCell>
                    <TableCell>
                      {item.target ? (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground">{item.target.type}</span>
                          {item.target.id ? (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 font-mono text-xs"
                              onClick={(event) => {
                                event.stopPropagation();
                                const targetId = item.target?.id ?? '';
                                void copy(targetId, copiedMessage, `audit-target-${targetId}`);
                              }}
                            >
                              {shortId(item.target.id)}
                              <CopyFeedbackIcon
                                copied={isCopied(`audit-target-${item.target.id}`)}
                                className="h-3 w-3 text-primary"
                              />
                            </button>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.projectId ? projectName(item.projectId) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{item.ip || '—'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {terminationCause ? <span className="text-sm">{terminationCause}</span> : null}
                        {item.reason ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <MessageSquare className="h-4 w-4 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent>{item.reason.slice(0, 100)}</TooltipContent>
                          </Tooltip>
                        ) : terminationCause ? null : (
                          <span className="text-muted-foreground">—</span>
                        )}
                        {item.merchantVisible ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Eye className="h-4 w-4 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent>{t('auditLog.merchantVisible')}</TooltipContent>
                          </Tooltip>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TooltipProvider>
      </div>

      <div className="mt-3 flex justify-center">
        {hasMore ? (
          <Button type="button" variant="outline" onClick={onLoadMore} disabled={isValidating}>
            {t('auditLog.loadMore')}
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">{t('auditLog.noMore')}</p>
        )}
      </div>
    </div>
  );
}
